import { saveAs } from 'file-saver';
import MarkdownIt from 'markdown-it';

/**
 * 导出文件工具类
 */
export default class ExportFile {
  /**
   * 将Markdown转换为HTML
   * @param {string} markdown - Markdown内容
   * @returns {string} HTML内容
   */
  static convertMarkdownToHtml(markdown) {
    const md = new MarkdownIt({
      html: true,
      breaks: true,
      linkify: true,
      typographer: true
    });
    return md.render(markdown);
  }

  /**
   * 导出Word文档
   * @param {string} content - Markdown格式的内容
   * @param {string} title - 文档标题
   * @param {Object} options - 配置选项
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async exportWord(content, title = 'AI分析报告', options = {}) {
    try {
      // 默认配置
      const defaultOptions = {
        fileName: `${title}_${new Date().toLocaleString().replace(/[\/\s:]/g, '')}.doc`,
        styles: `
          body { 
            font-family: "Microsoft YaHei", Arial, sans-serif; 
            line-height: 1.8; 
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
            text-align: left !important;
          }
          * {
            text-align: left !important;
          }
          h1 { 
            color: #333; 
            border-bottom: 2px solid #409EFF;
            padding-bottom: 10px;
            margin-bottom: 20px;
            font-size: 30px;
            text-align: left !important;
          }
          h2 {
            color: #444;
            font-size: 28px;
            margin-top: 25px;
            text-align: left !important;
          }
          h3 {
            color: #555;
            font-size: 28px;
            text-align: left !important;
          }
          h4 {
            color: #555;
            font-size: 26px;
            text-align: left !important;
          }
          h5 {
            color: #555;
            font-size: 24px;
            text-align: left !important;
          }
          p { 
            margin: 12px 0;
            color: #333;
            text-align: left !important;
          }
          ul, ol {
            margin: 10px 0;
            padding-left: 20px;
            text-align: left !important;
          }
          li {
            margin: 5px 0;
            text-align: left !important;
          }
          blockquote {
            margin: 15px 0;
            padding: 10px 15px;
            border-left: 4px solid #409EFF;
            background-color: #f8f9fa;
            color: #666;
            text-align: left !important;
          }
          code {
            background-color: #f6f8fa;
            padding: 2px 5px;
            border-radius: 3px;
            font-family: Consolas, monospace;
            text-align: left !important;
          }
          pre {
            background-color: #f6f8fa;
            padding: 15px;
            border-radius: 5px;
            overflow-x: auto;
            text-align: left !important;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 15px 0;
            text-align: left !important;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left !important;
          }
          th {
            background-color: #f5f7fa;
            text-align: left !important;
          }
          strong, b, em, i, span, a {
            text-align: left !important;
          }
          div {
            text-align: left !important;
          }
        `
      };

      // 合并选项
      const finalOptions = { ...defaultOptions, ...options };
      // 将Markdown转换为HTML
      const htmlContent = this.convertMarkdownToHtml(content);

      // 构建完整的HTML文档
      const docContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${title}</title>
          <style>
            ${defaultOptions.styles}
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          ${htmlContent}
        </body>
        </html>
      `;

      // 创建Blob对象
      const blob = new Blob([docContent], { 
        type: 'application/msword;charset=utf-8' 
      });

      // 下载文件
      saveAs(blob, finalOptions.fileName);

      return {
        success: true,
        message: '导出成功'
      };
    } catch (error) {
      console.error('导出文件失败:', error);
      return {
        success: false,
        message: '导出失败',
        error
      };
    }
  }

  /**
   * 导出Markdown文件
   * @param {string} content - Markdown内容
   * @param {string} title - 文件标题
   * @param {Object} options - 配置选项
   * @returns {Promise<void>}
   */
  static async exportMarkdown(content, title = 'AI分析报告', options = {}) {
    try {
      const defaultOptions = {
        fileName: `${title}_${new Date().toLocaleString().replace(/[\/\s:]/g, '')}.md`
      };

      const finalOptions = { ...defaultOptions, ...options };
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
      
      saveAs(blob, finalOptions.fileName);

      return {
        success: true,
        message: '导出成功'
      };
    } catch (error) {
      console.error('导出文件失败:', error);
      return {
        success: false,
        message: '导出失败',
        error
      };
    }
  }

  /**
   * 导出HTML文件
   * @param {string} content - HTML内容
   * @param {string} title - 文件标题
   * @param {Object} options - 配置选项
   * @returns {Promise<void>}
   */
  static async exportHtml(content, title = 'AI分析报告', options = {}) {
    try {
      const defaultOptions = {
        fileName: `${title}_${new Date().toLocaleString().replace(/[\/\s:]/g, '')}.html`,
        styles: `
          body { 
            font-family: Arial, sans-serif; 
            line-height: 1.6; 
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 { 
            color: #333; 
            border-bottom: 1px solid #eee;
            padding-bottom: 10px;
          }
          p { 
            margin-bottom: 10px; 
            color: #666;
          }
        `
      };

      const finalOptions = { ...defaultOptions, ...options };
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${title}</title>
          <style>
            ${finalOptions.styles}
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          ${content.replace(/\n/g, '<br>')}
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      saveAs(blob, finalOptions.fileName);

      return {
        success: true,
        message: '导出成功'
      };
    } catch (error) {
      console.error('导出文件失败:', error);
      return {
        success: false,
        message: '导出失败',
        error
      };
    }
  }

  /**
   * 将内容复制到剪贴板
   * @param {string} content - 要复制的内容
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async copyToClipboard(content) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      
      return {
        success: true,
        message: '复制成功'
      };
    } catch (error) {
      console.error('复制失败:', error);
      return {
        success: false,
        message: '复制失败',
        error
      };
    }
  }

  /**
   * 导出Excel文件
   * @param {Array} data - 需要导出的数据数组
   * @param {Array} headers - 表头数组，格式为 [{title: '标题', key: '对应的字段名'}]
   * @param {string} fileName - 导出的文件名
   * @param {string} sheetName - Excel表格的工作表名称
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async exportExcel(data, headers, fileName = 'data_export', sheetName = 'Sheet1') {
    try {
      if (!Array.isArray(data) || !Array.isArray(headers)) {
        throw new Error('数据格式错误，data和headers必须是数组');
      }

      // 完整文件名
      const fullFileName = `${fileName}_${new Date().toLocaleString().replace(/[\/\s:]/g, '')}.xlsx`;
      
      // 创建CSV内容
      let csvContent = '\ufeff'; // 添加BOM标记，保证Excel正确识别中文
      
      // 添加表头
      const headerRow = headers.map(header => {
        // 将表头中的英文双引号替换为两个双引号，避免CSV格式错误
        const title = header.title.replace(/"/g, '""');
        // 将所有表头用双引号包裹，防止特殊字符导致的CSV解析问题
        return `"${title}"`;
      }).join(',');
      csvContent += headerRow + '\r\n';
      
      // 添加数据行
      data.forEach(row => {
        const dataRow = headers.map(header => {
          // 获取单元格数据
          let cellData = row[header.key] !== undefined ? row[header.key] : '';
          
          // 如果是字符串，处理特殊字符
          if (typeof cellData === 'string') {
            // 将英文双引号替换为两个双引号，避免CSV格式错误
            cellData = cellData.replace(/"/g, '""');
            // 将所有数据用双引号包裹，防止特殊字符导致的CSV解析问题
            cellData = `"${cellData}"`;
          } else if (cellData === null || cellData === undefined) {
            cellData = '""';
          } else {
            // 非字符串类型，直接包裹
            cellData = `"${cellData}"`;
          }
          
          return cellData;
        }).join(',');
        
        csvContent += dataRow + '\r\n';
      });
      
      // 创建Blob对象，使用CSV MIME类型
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      
      // 使用file-saver保存文件
      saveAs(blob, fullFileName);
      
      return {
        success: true,
        message: '导出成功'
      };
    } catch (error) {
      console.error('导出Excel失败:', error);
      return {
        success: false,
        message: '导出失败',
        error
      };
    }
  }

  /**
   * 将JSON数据转换为Excel并导出
   * @param {Array} jsonData - JSON数据数组
   * @param {string} fileName - 导出的文件名(不含扩展名)
   * @param {string} sheetName - 工作表名称
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async exportJsonToExcel(jsonData, fileName = 'data_export', sheetName = 'Sheet1') {
    try {
      if (!Array.isArray(jsonData) || jsonData.length === 0) {
        throw new Error('数据格式错误，jsonData必须是非空数组');
      }
      
      // 自动从第一个对象生成表头
      const firstItem = jsonData[0];
      const headers = Object.keys(firstItem).map(key => ({
        title: key, // 使用字段名作为标题
        key: key
      }));
      
      // 调用exportExcel方法
      return await this.exportExcel(jsonData, headers, fileName, sheetName);
    } catch (error) {
      console.error('导出Excel失败:', error);
      return {
        success: false,
        message: '导出失败',
        error
      };
    }
  }
}